import { FC, useEffect, useMemo } from 'react'
import cn from 'classnames'
import { NavLink } from 'react-router-dom'

import style from '@src/components/MyTariffPlan/MyTariffPlan.module.scss'
import { Line } from '@src/components/Line/Line'
import { Button } from '@src/components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectUserInfo,
  selectUserLastPayment,
  selectUserServices,
  selectUserSumToPay,
} from '@src/store/ducks/user/selectors'
import moment from 'moment'
import { setPageTitle } from '@src/store/ducks/app/reducer'
import { AppDispatch } from '@src/store/store'
import { selectRecurrentCard } from '@src/store/ducks/pay/selectors'
import 'moment/locale/ru'
import { parsPrice } from '@src/lib/utils'

type MyTariffPlanProps = {
  buttons?: boolean
  serviceId?: number
}

moment.locale('ru')

export const MyTariffPlan: FC<MyTariffPlanProps> = ({ buttons, serviceId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { tariff, services, futureServices } = useSelector(selectUserServices)
  const sumToPay = useSelector(selectUserSumToPay)
  const lastPayment = useSelector(selectUserLastPayment)
  const user = useSelector(selectUserInfo)
  const recurrentCard = useSelector(selectRecurrentCard)

  useEffect(() => {
    dispatch(setPageTitle('Мой тарифный план'))
  }, [dispatch])

  const tariffInfo = useMemo(() => {
    const result = tariff?.map((item, i) => {
      const tmp: any = {
        ...item,
        detailsBlock: [
          {
            title: 'Ежемесячная плата',
            // eslint-disable-next-line max-len
            cost: item?.price,
            oldCost: tariff && item?.price < item?.defaultPrice ? item?.defaultPrice : null,
            discountDate: item?.DiscountDate,
            discountDateEnd: item?.discountDateEnd,
          },
        ],
      }
      if (i === 0) {
        tmp.detailsBlock.push(
          { title: 'Рекомендуемый платёж', cost: sumToPay },
          { title: 'Последний платёж', cost: lastPayment.summ, lastPay: `(${lastPayment.date})` },
        )
      }
      return tmp
    })

    if (serviceId) {
      return result?.filter((item) => item.serviceId === serviceId)
    }

    return result
  },
  [tariff, serviceId, sumToPay, lastPayment])

  return (
    <>
      {tariffInfo?.map((item, i) => (
        <div className={style.myTariffPlan} key={item.serviceId}>
          {i === 0 && (
            <h3 className={style.title}>{user.isSubscriber ? 'Действующая подписка' : 'Мой тарифный план'}</h3>
          )}
          <h4 className={style.secondTitle}>
            {item.goodName}
            {item.dateEnd.trim() && (
              <span className={style.secondTitleEnd}>
                {' '}
                до (
                {moment(item.dateEnd, 'DD.MM.YYYY').format('DD.MM.YYYY')}
                )
              </span>
            )}
          </h4>

          {!item.active && !item.block?.active && (
            <div className={style.tariff_financial_block}>
              {user.isSubscriber
                ? `Для активации подписки необходимо внести ${sumToPay} бел. руб.`
                : 'Услуга находится в финансовой блокировке'}
            </div>
          )}

          {item.block && (
            <div className={style.tariff_block}>
              {item.block.goodName}
              {' '}
              <span>
                (
                {item.block.dateStart.trim() && (
                  <>
                    с
                    {' '}
                    {moment(item.block.dateStart, 'DD.MM.YYYY HH:mm:ss').format('DD.MM.YYYY HH:mm')}
                  </>
                )}
                {item.block.dateEnd.trim() && (
                  <>
                    {' '}
                    до
                    {' '}
                    {moment(item.block.dateEnd, 'DD.MM.YYYY HH:mm:ss').format('DD.MM.YYYY HH:mm')}
                  </>
                )}
                )
              </span>
            </div>
          )}
          <Line />
          <div className={style.detailsPlan}>
            <div className={style.left}>
              {item.detailsBlock.map((el: any) => (
                !!el.cost && (
                  <div key={el.title} className={style.detailsBlock}>
                    <div className={style.title}>{el.title}</div>
                    <div className={style.costContainer}>
                      <div className={cn(style.cost, el.oldCost && style.textDecor)}>
                        {parsPrice(el.oldCost ? el.oldCost : el.cost)}
                        <span> р.</span>
                      </div>
                      {el.oldCost && (
                        <>
                          <div className={style.newCost}>
                            {parsPrice(el.cost)}
                            <span> р.</span>
                          </div>
                          {el.discountDate && (
                            <div className={style.costDiscountDate}>
                              (по
                              {' '}
                              {el.discountDate}
                              )
                            </div>
                          )}
                          {el.discountDateEnd && (
                            <div className={style.costDiscountDate}>
                              (по
                              {' '}
                              {el.discountDateEnd}
                              )
                            </div>
                          )}
                        </>
                      )}
                      {el.lastPay && <div className={style.lastPay}>{el.lastPay}</div>}
                    </div>
                  </div>
                )
              ))}
              {recurrentCard?.status === '1' && (
                (() => {
                  const dayOfPay = +recurrentCard.dayOfPay
                  const today = moment().date()
                  const shouldAddMonth = today < dayOfPay ? 0 : 1
                  const nextDate = moment(`${moment().year()}-${moment().format('MM')}-${dayOfPay}`)
                    .add(shouldAddMonth, 'months')
                    .format('DD MMMM')

                  // Логируем всё, что важно
                  console.log('--- Debug recurrentCard ---')
                  console.log('recurrentCard:', recurrentCard)
                  console.log('recurrentCard.dayOfPay:', recurrentCard.dayOfPay)
                  console.log('typeof dayOfPay:', typeof dayOfPay)
                  console.log('today (moment().date()):', today)
                  console.log('dayOfPay:', dayOfPay)
                  console.log('shouldAddMonth:', shouldAddMonth ? 'Да (+1 месяц)' : 'Нет')
                  console.log('nextDate:', nextDate)
                  console.log('-----------------------------')

                  // Возвращаем JSX
                  return (
                    <div className={style.recurrentPay}>
                      {nextDate}
                      {' '}
                      {user.isSubscriber ? 'подписка будет продлена автоматически' : 'услуга будет оплачена автоматически'}
                    </div>
                  )
                })()
              )}
              {/* {recurrentCard?.status === '1' && ( */}
              {/*  <div className={style.recurrentPay}> */}
              {/*    {moment(`${moment().year()}-${moment().format('MM')}-${recurrentCard.dayOfPay}`).add(moment().date() < +recurrentCard.dayOfPay ? 1 : 0, 'months').format('DD MMMM')} */}
              {/*    {' '} */}
              {/*    {user.isSubscriber ? 'подписка будет продлена автоматически' : 'услуга будет оплачена автоматически'} */}
              {/*  </div> */}
              {/* )} */}
            </div>
            <div className={style.right}>
              {!!services.filter((el) => el.parentId === item.serviceId).length && (
                <div>
                  <h4>Предоставляемые услуги в тарифном плане</h4>
                  <ul>
                    {services.filter((el) => el.parentId === item.serviceId)
                      .map((el) => (
                        <li key={el.goodName} className={style.service}>
                          {el.goodName}
                          {!!el.price && (
                            <span className={style.service_price}>
                              (
                              {el.price}
                              p.)
                            </span>
                          )}
                          {el.dateEnd && el.dateEnd !== ' ' && (
                            <span className={style.service_date}>
                              до (
                              {moment(el.dateEnd, 'DD.MM.YYYY').format('DD.MM.YYYY')}
                              )
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {!!futureServices.filter((el) => el.parentId === item.serviceId).length && (
                <div>
                  <h4>Запланированные услуги в тарифном плане</h4>
                  <ul>
                    {futureServices.filter((el) => el.parentId === item.serviceId).map((el) => (
                      <li key={el.goodName} className={style.service}>
                        <span>
                          {el.goodName}
                          <span className={style.service_date}>
                            (
                            {moment(el.dateStart, 'DD.MM.YYYY').format('DD.MM.YYYY')}
                            )
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          {buttons && (
            <div className={style.btnBlock}>
              <NavLink
                to={`/additional-services/${item.serviceId}`}
              >
                <Button type="secondary" className={style.btnSize} outline>Добавить услугу</Button>
              </NavLink>
              {!user.isSubscriber && (
                <NavLink
                  to={`/change-tariff/${item.serviceId}`}
                >
                  <Button type="secondary" className={style.btnSize} outline>
                    {user.isSubscriber ? 'Сменить подписку' : 'Сменить тарифный план'}
                  </Button>
                </NavLink>
              )}
            </div>
          )}
        </div>
      ))}
    </>
  )
}
